import { variant } from '@staccx/bento/lib/theming/themeContants'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { ApplicationCustomerDetails } from './CreateFinanceApplicationSteps/ApplicationCustomerDetails'
import { ApplicationAssetDetails } from './CreateFinanceApplicationSteps/ApplicationAssetDetails'
import { ApplicationCalculator } from './CreateFinanceApplicationSteps/ApplicationCalculator'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useUserContext } from '../../../context/UserProvider'
import { useQueryClient } from 'react-query'
import { Mapdata } from './CreateFinanceApplicationSteps/Mapdata'

export function CreateFinanceApplication({ lastId }) {
  const method = useForm({
    defaultValues: financeApplicationDefaultValues(),
    resolver: zodResolver(financeApplicationResolver())
  })

  const user = useUserContext() as any

  const queryClient = useQueryClient()
  const [submitted, setSubmitted] = useState(false)
  function onSubmit(data) {
    console.log('submitted:', data)
    submitTo(data)
  }

  const newId = parseInt(lastId) + 1

  async function submitTo(data) {
    const formatBody = Mapdata(data, newId)
    try {
      const response = await fetch('/api/flow-definitions/initiate-contract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.newUserData?.access_token}`
        },
        body: JSON.stringify(formatBody)
      })
        .then((res) => res.json())
        .then((data) => {
          queryClient.invalidateQueries('flows')
          setSubmitted(true)
          return data
        })
      return response
    } catch (error) {
      console.error(error)
    }
  }

  const [currentStep, setCurrentStep] = useState(0)

  const progressLength = ((currentStep + 1) / 3) * 100
  console.log(method.formState.errors)
  if (submitted) {
    return (
      <div className="min-h-[675px] flex w-full flex-col gap-4 items-center justify-center pb-24">
        <div className="submitPopin text-yellow-500 ">
          <SubmitCheckmark />
        </div>
        <p className="text-xl submitPopup">Credit Decision: Manual Handling</p>
        <p className="text-xs submitPopup">You can safely close the window</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8 p-4">
      <div className="flex flex-col gap-2">
        <div className="w-full text-sm flex items-center justify-between">
          {currentStep === 0 && <p>Calculator</p>}
          {currentStep === 1 && <p>Asset Details</p>}
          {currentStep === 2 && <p>Customer Details</p>}
          <p>{currentStep + 1} / 3</p>
        </div>
        <div className="w-full border-[1px] border-primary h-2 relative">
          <div className="transition-all ease-in-out w-fit">
            <div
              className="bg-primary h-full transition-all  ease-in-out absolute left-0"
              style={{ width: `${progressLength}%` }}
            ></div>
          </div>
        </div>
      </div>
      <FormProvider {...method}>
        <form onSubmit={method.handleSubmit(onSubmit)}>
          {currentStep === 0 && <ApplicationCalculator onNext={() => setCurrentStep(currentStep + 1)} {...method} />}
          {currentStep === 1 && (
            <ApplicationAssetDetails
              trigger={method.trigger}
              setValue={method.setValue}
              onNext={() => setCurrentStep(currentStep + 1)}
              onBack={() => setCurrentStep(currentStep - 1)}
            />
          )}
          {currentStep === 2 && <ApplicationCustomerDetails onBack={() => setCurrentStep(currentStep - 1)} />}
        </form>
      </FormProvider>
      <Summary method={method} />
    </div>
  )
}

export function formatMoney(amount: number) {
  return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK' }).format(amount)
}

export function SubmitCheckmark() {
  return (
    <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_19_614)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M25 3.125C12.9188 3.125 3.125 12.9188 3.125 25C3.125 37.0812 12.9188 46.875 25 46.875C37.0812 46.875 46.875 37.0812 46.875 25C46.875 12.9188 37.0812 3.125 25 3.125ZM0 25C0 11.1929 11.1929 0 25 0C38.8071 0 50 11.1929 50 25C50 38.8071 38.8071 50 25 50C11.1929 50 0 38.8071 0 25Z"
          fill="currentColor"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M37.5 15.625C38.1102 16.2352 38.1102 17.2245 37.5 17.8347L22.9799 32.3549C22.3697 32.9651 21.3803 32.9651 20.7701 32.3549L12.9576 24.5424C12.3475 23.9322 12.3475 22.9428 12.9576 22.3326C13.5678 21.7225 14.5572 21.7225 15.1674 22.3326L21.875 29.0403L35.2903 15.625C35.9005 15.0148 36.8898 15.0148 37.5 15.625Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_19_614">
          <rect width="50" height="50" fill="currentColor" />
        </clipPath>
      </defs>
    </svg>
  )
}

export function Summary({ method }) {
  const financedAmount =
    Number(method.watch('calculator.purchasePrice')) - Number(method.watch('calculator.downPayment'))
  const termAmount =
    (financedAmount / Number(method.watch('calculator.loanTerms'))) *
    (1 + Number(method.watch('calculator.nominalInterestRate')) / 100)

  const customerName = `${method.watch('customerDetails.firstName')} ${method.watch('customerDetails.lastName')}`
  const formattedCustomerName = customerName.length > 1 ? customerName : '-- --'
  const customerPhone =
    method.watch('customerDetails.phoneNumber').length > 1 ? method.watch('customerDetails.phoneNumber') : '-- --'

  const carMake = method.watch('assetDetails.make').length > 1 ? method.watch('assetDetails.make') : '-- --'
  const carModel = method.watch('assetDetails.model').length > 1 ? method.watch('assetDetails.model') : '-- --'
  return (
    <div className="border-t [&>*]:w-[200px] border-t-gray-300 pt-4 flex items-center justify-between">
      <div>
        <p className="text-sm">Financed Amount</p>
        <p className="text-3xl font-bold">{formatMoney(financedAmount)}</p>
        <p className="text-xs">Term: {formatMoney(termAmount)}</p>
      </div>
      <div>
        <p className="text-sm">Asset Details</p>
        <p className="text-3xl font-bold line-clamp-1 text-ellipsis">{carMake}</p>
        <p className="text-xs">{carModel}</p>
      </div>
      <div className="text-right">
        <p className="text-sm">Customer Details</p>
        <p className="text-3xl font-bold line-clamp-1 text-ellipsis">{formattedCustomerName}</p>
        <p className="text-xs">{customerPhone}</p>
      </div>
    </div>
  )
}

export function financeApplicationResolver() {
  return z.object({
    calculator: z.object({
      purchaseType: z.string(),
      purchasePrice: z.string().min(1, 'Enter a purchase price'),
      registrationFee: z.string().min(1, 'Enter a registration fee'),
      downPayment: z.string().min(1, 'Enter a down payment'),
      nominalInterestRate: z.string().min(1, 'Enter a nominal interest rate'),
      loanTerms: z.number(),
      calculatedResidualValue: z.string().optional(),
      feeIncVat: z.string().optional()
    }),
    assetDetails: z.object({
      regNo: z.number().optional(),
      make: z.string().min(1, 'Enter a valid make'),
      model: z.string().min(1, 'Enter a valid model'),
      modelYear: z.number().min(1, 'Enter a valid model year'),
      variant: z.string().min(1, 'Enter a valid variant'),
      bodyType: z.string().optional(),
      engineType: z.string().min(1, 'Enter a valid engine type'),
      imageUrl: z.string().optional(),
      registrationDate: z.string().optional()
    }),
    customerDetails: z.object({
      firstName: z.string().min(1, 'Enter a valid first name'),
      lastName: z.string().min(1, 'Enter a valid last name'),
      address: z.string().min(1, 'Enter a valid address'),
      zipCode: z.string().min(1, 'Enter a valid zip code'),
      city: z.string().min(1, 'Enter a valid city'),
      phoneNumber: z.string().min(1, 'Enter a valid phone number')
    })
  })
}

export function financeApplicationDefaultValues() {
  return {
    calculator: {
      purchaseType: 'loan',
      purchasePrice: '',
      registrationFee: '',
      downPayment: '',
      nominalInterestRate: '4.4',
      loanTerms: 72,
      calculatedResidualValue: '',
      feeIncVat: ''
    },
    assetDetails: {
      regNo: '',
      make: '',
      model: '',
      modelYear: '',
      variant: '',
      engineType: '',
      imageUrl: ''
    },
    customerDetails: {
      ssn: '',
      firstName: '',
      lastName: '',
      address: '',
      zipCode: '',
      city: '',
      phoneNumber: '',
      email: ''
    }
  }
}
